import { useCallback, useMemo, useState } from 'react';

import { Option } from '@/common/types';

import useProcessingRuleById from './useProcessingRuleById';
import useProcessingRulesList from './useProcessingRulesList';

export const PROCESSING_RULES_SORT = 'id,DESC';
const PROCESSING_RULES_PER_PAGE = 100;

interface Props {
  isUsed?: boolean;
  mergeRuleId?: string;
  sort?: string;
}

const useProcessingRulesPagination = ({ isUsed, mergeRuleId, sort = PROCESSING_RULES_SORT }: Props = {}) => {
  const [activePage, setActivePage] = useState<number>(1);

  const {
    data,
    list: processingRulesList,
    isLoading: isLoadingRules,
    isFetching: isFetchingRules,
    page,
    totalPages,
  } = useProcessingRulesList(
    {
      perPage: PROCESSING_RULES_PER_PAGE,
      page: activePage,
      sort,
    },
    { isUsed: !!isUsed }
  );

  const { ruleOption: mergedRuleOption } = useProcessingRuleById(mergeRuleId);

  const mergedProcessingRulesList = useMemo<Option[]>(() => {
    if (!mergedRuleOption?.length) return processingRulesList;

    const filteredList = processingRulesList?.filter((item) => item.key !== mergedRuleOption[0].key) || [];
    return [mergedRuleOption[0], ...filteredList];
  }, [mergedRuleOption, processingRulesList]);

  const isLoadingAndFetching = isLoadingRules || isFetchingRules;
  const isMainListEmpty = data === undefined;
  const isFieldLoading = isLoadingAndFetching || (isMainListEmpty && !processingRulesList.length);

  const handleLoadMoreRules = useCallback(() => {
    if (page + 1 < totalPages) {
      setActivePage(page + 1);
    }
  }, [page, totalPages]);

  const resetPagination = useCallback(() => {
    setActivePage(1);
  }, []);

  return {
    data,
    processingRulesList: mergedProcessingRulesList,
    isLoadingRules: isLoadingAndFetching,
    isFieldLoading,
    handleLoadMoreRules,
    resetPagination,
  };
};

export default useProcessingRulesPagination;
